import React, { FC, useCallback, useState } from "react";

import { UseFormReturn } from "react-hook-form";
import * as yup from "yup";

import { Breadcrumb, PageTemplateOtherType } from "@app/types";

import { Inline } from "@fermions";

import Form from "@components/atoms/Form";
import Loading from "@components/molecules/Loading/Loading";

import BoxTemplate from "../BoxTemplate";
import PageTemplate from "../PageTemplate";
import "./FormPageTemplate.scss";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "text"
  | "text-primary"
  | "danger";

interface FormConfig {
  contents: React.ReactElement;
  withoutProvider?: boolean;
  formMethods?: UseFormReturn<Record<string, unknown>>;
  handleSubmit: () => void;
  handleCancel?: () => void;
  yupSchema?: yup.AnySchema;
  submitLabel?: string;
  submitButtonVariant?: ButtonVariant;
  submitIconName?: string;
  cancelLabel?: string;
  defaultValues?: Record<string, unknown>;
  autoComplete?: string;
  disabled?: boolean;
  customFormClassName?: string;
  renderPageActions?: (isValid: boolean) => React.ReactNode;
}

interface FormPageTemplateProps {
  title: string;
  sticky?: boolean;
  breadcrumbs?: Breadcrumb[];
  form: FormConfig;
  isLoading?: boolean;
  other?: Omit<PageTemplateOtherType, "project"> & {
    project?: { id: string | number; name: string };
  };
  renderRawForm?: boolean;
  alwaysShowBreadcrumbs?: boolean;
}

const FormPageTemplate: FC<FormPageTemplateProps> = ({
  title,
  sticky,
  breadcrumbs,
  isLoading,
  other,
  form,
  renderRawForm,
  alwaysShowBreadcrumbs
}) => {
  const getForm = () => {
    if (renderRawForm) {
      return form?.contents ?? <></>;
    }
    return (
      <BoxTemplate className="ot-form-page-template">
        {form?.contents || <></>}
      </BoxTemplate>
    );
  };

  const [actionButtonsPortal, setActionButtonsPortal] =
    useState<HTMLElement | null>(null);
  const actionButtonsRef = useCallback((node: HTMLElement | null) => {
    setActionButtonsPortal(node);
  }, []);

  return (
    <Form {...form} actionButtonsPortal={actionButtonsPortal}>
      <PageTemplate
        header={{
          title,
          sticky: sticky ?? false,
          breadcrumbs,
          alwaysShowBreadcrumbs,
          actions: (
            <Inline
              className="ot-title__actions"
              wrap
              alignment="right"
              ref={actionButtonsRef}
            />
          )
        }}
        other={other}
        body={{
          primary: isLoading ? <Loading message="loading" /> : getForm()
        }}
      />
    </Form>
  );
};

export default FormPageTemplate;
